"use client";

import Link from "next/link";
import { useId } from "react";
import { GoogleSignInButton } from "@/components/forms/google-sign-in-button";
import { buttonClass } from "@/components/ui/button";

/**
 * The `aha-signup-ask` post-apply card ("save this job + get similar ones"),
 * shared by the job-detail apply trigger (`JobIntentSignup`) and the feed-card
 * apply trigger (`FeedAhaSignupAsk`). One copy, one look, so the experiment's
 * treatment is the same ask whichever apply the guest clicked.
 */

export type AhaApplyAskCopy = {
	eyebrow: string;
	title: string;
	body: string;
	google: string;
	googleFallback: string;
	email: string;
	skip: string;
	saved: string;
};

const ID: AhaApplyAskCopy = {
	eyebrow: "Lamaran dibuka di tab baru",
	title: "Simpan lowongan ini + dapat yang mirip",
	body: "Daftar gratis sekali. Lowongan ini tersimpan di akun kamu, dan kami kirim lowongan serupa ke email kamu.",
	google: "Masuk dengan Google",
	googleFallback: "Koneksi bermasalah. Coba pakai email saja.",
	email: "Pakai email saja",
	skip: "Lewati",
	saved:
		"Lamaran ini sudah tersimpan. Lowongan baru masuk ke email kamu tiap pagi.",
};

const EN: AhaApplyAskCopy = {
	eyebrow: "Application opened in a new tab",
	title: "Save this job + get similar ones",
	body: "Sign up free, once. We save this job to your account and email you jobs like it.",
	google: "Continue with Google",
	googleFallback: "Connection problem. Try email instead.",
	email: "Use email instead",
	skip: "Skip",
	saved:
		"This application is saved. New jobs will land in your inbox every day.",
};

export function ahaApplyAskCopy(locale: string): AhaApplyAskCopy {
	return locale === "en" ? EN : ID;
}

const SKIP_KEY = "ld_job_intent_signup_skipped";

/** Skipped in this tab session: no apply-side ask again until it ends. */
export function wasAhaApplyAskSkipped(): boolean {
	try {
		return window.sessionStorage.getItem(SKIP_KEY) === "1";
	} catch {
		return false;
	}
}

export function markAhaApplyAskSkipped(): void {
	try {
		window.sessionStorage.setItem(SKIP_KEY, "1");
	} catch {
		/* not remembered; card still closes */
	}
}

type Props = {
	locale: string;
	/** Post-signup return path (carries `?src=job_intent_signup`). */
	callbackURL: string;
	showEyebrow: boolean;
	testId: string;
	placement: string;
	className?: string;
	onAskClicked: (method: "google" | "email") => void;
	onSkip: () => void;
};

export function AhaApplySignupCard({
	locale,
	callbackURL,
	showEyebrow,
	testId,
	placement,
	className,
	onAskClicked,
	onSkip,
}: Props) {
	const headingId = useId();
	const t = ahaApplyAskCopy(locale);
	const emailHref = `/${locale}/signup?callbackURL=${encodeURIComponent(callbackURL)}`;
	return (
		<section
			aria-labelledby={headingId}
			data-testid={testId}
			data-placement={placement}
			className={`mt-3 rounded-xl border border-border bg-card p-4 text-left${className ? ` ${className}` : ""}`}
		>
			{showEyebrow ? (
				<p className="text-xs text-muted-foreground">{t.eyebrow}</p>
			) : null}
			<h3 id={headingId} className="mt-1 text-sm font-semibold text-foreground">
				{t.title}
			</h3>
			<p className="mt-1 text-xs text-muted-foreground">{t.body}</p>
			<div className="mt-3 flex flex-col gap-2">
				<GoogleSignInButton
					callbackURL={callbackURL}
					label={t.google}
					magicLinkFallbackLabel={t.googleFallback}
					context="signup"
					onPress={() => onAskClicked("google")}
				/>
				<div className="flex items-center justify-between gap-2">
					<Link
						href={emailHref}
						onClick={() => onAskClicked("email")}
						className="rounded text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						{t.email}
					</Link>
					<button
						type="button"
						onClick={onSkip}
						className={buttonClass({
							variant: "ghost",
							size: "xs",
							shape: "lg",
						})}
					>
						{t.skip}
					</button>
				</div>
			</div>
		</section>
	);
}
